import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress, Alert, Button, Typography } from '@mui/material';
import { OrderHeader } from './components';
import IncrementalOrderForm from './components/IncrementalOrderForm';
import ItemConfigurationPanelWrapper from './components/ItemConfigurationPanelWrapper';
import { Product, Order } from '../order';
import { orderService } from '../orderService';
// Removed Redux imports - now using fresh API data instead
import { useOrderData } from './hooks/useOrderData';
import CloseConfirmationDialogue from '../../../components/common/__tests__/CloseConfirmationDialogue';
import { hasAnyPermission } from '../../../utils/permissions';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';


// Props for UnifiedOrderPage
interface UnifiedOrderPageProps {
  mode?: 'create' | 'edit';
  preSelectedCustomer?: any;
  preSelectedSite?: any;
  preSelectedSites?: any[];
  onOrderCreated?: (order: any) => void;
  onOrderUpdated?: (order: any) => void;
  onClose?: () => void;
}

// Main Unified Order Page Component
const UnifiedOrderPage: React.FC<UnifiedOrderPageProps> = ({ 
  mode = 'create',
  preSelectedCustomer,
  preSelectedSite,
  preSelectedSites,
  onOrderCreated,
  onOrderUpdated,
  onClose
}) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  // Check if user has any permissions at all
  const user = useSelector((state: RootState) => state.auth.user);
  const userPermissions = user?.permissions;
  const userRole = user?.role;
  const userRoles = user?.roles;
  const hasAnyPermissions = hasAnyPermission(userPermissions, userRole, userRoles);
  
  // State for edit mode
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(mode === 'edit');
  const [error, setError] = useState<string | null>(null);
  
  // State for UI
  const [showItemPanel, setShowItemPanel] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [configuredProducts, setConfiguredProducts] = useState<Product[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isProductEditMode, setIsProductEditMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [closeDialog, setCloseDialog] = useState(false);
  
  // Custom hook for order data
  const { 
    customers, 
    sites, 
    selectedCustomer, 
    selectedSite, 
    handleCustomerChange, 
    handleSiteChange 
  } = useOrderData(
    mode === 'edit' ? order?.customer : preSelectedCustomer, 
    mode === 'edit' ? (order as any)?.site : preSelectedSite,
    preSelectedSites
  );

  // Debug logging for pre-selection
  console.log('🔄 UnifiedOrderPage pre-selection debug:', {
    mode,
    orderExists: !!order,
    orderCustomer: order?.customer,
    orderSite: (order as any)?.site,
    preSelectedCustomer,
    preSelectedSite,
    selectedCustomer,
    selectedSite
  });

  // Load order data for edit mode
  useEffect(() => {
    console.log('🔄 useEffect triggered - mode:', mode, 'id:', id);
    if (mode === 'edit' && id) {
      const loadOrder = async () => {
        try {
          setLoading(true);
          console.log('🔄 Loading order with ID:', id);
          const response = await orderService.getOrder(id);
          console.log('🔄 Order service response:', response);
          
          if (response.success) {
            console.log('✅ Order loaded successfully:', response.data);
            console.log('✅ Order ID:', response.data.id);
            console.log('✅ Order products:', response.data.products?.length || 0);
            setOrder(response.data);
            
            // Customer and site will be pre-populated automatically by useOrderData hook
            console.log('✅ Order loaded, customer and site will be pre-populated automatically');
          } else {
            console.error('Order service returned error:', response.error);
            setError(response.error?.message || 'Failed to load order');
          }
        } catch (error) {
          console.error('Error loading order:', error);
          setError('Failed to load order');
        } finally {
          setLoading(false);
        }
      };

      loadOrder();
    }
  }, [mode, id]); // Removed selectedCustomer, selectedSite, handleCustomerChange, handleSiteChange from dependencies

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/orders');
    }
  };

  const handleViewOrderDetails = () => {
    // Navigate to order details page
    const orderId = order?.id || id;
    if (orderId) {
      navigate(`/orders/${orderId}`);
    }
  };

  const handleProductAdded = (product: Product) => {
    setSelectedProduct(product);
    setShowItemPanel(true);
    setIsProductEditMode(false);
  };

  const handleProductEdit = (product: Product) => {    
    setSelectedProduct(product);    
    setShowItemPanel(true);    
    setIsProductEditMode(true);
  };

  const handleItemConfigurationComplete = async (updatedProduct: Product) => {
    console.log('🔄 Product configuration completed:', updatedProduct);
    console.log('🔄 isProductEditMode:', isProductEditMode, 'mode:', mode);
    
    if (mode === 'create') {
      // In create mode, add to configured products and let IncrementalOrderForm handle order creation
      console.log('🔄 Adding product to configured products, IncrementalOrderForm will handle order creation');
      
      setConfiguredProducts(prev => {
        const existingIndex = prev.findIndex(p => p.id === updatedProduct.id);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = updatedProduct;
          return updated;
        } else {
          return [...prev, updatedProduct];
        }
      });
      
      // Close the panel
      setShowItemPanel(false);
      setSelectedProduct(null);
      setIsProductEditMode(false);
    } else {
      // For edit mode, ItemConfigurationPanel already handled the API call
      // Just close the panel and refresh the UI
      console.log('🔄 Edit mode - ItemConfigurationPanel already handled API call, just closing panel');
      
      // Close the panel
      setShowItemPanel(false);
      setSelectedProduct(null);
      setIsProductEditMode(false);
      
      // Trigger refresh to ensure UI updates with fresh data
      setRefreshTrigger(prev => prev + 1);
    }
  };

  const handleProductUpdate = async (updatedProduct: Product) => {
    console.log('🔄 handleProductUpdate called with:', updatedProduct);
    console.log('🔄 mode:', mode, 'order exists:', !!order, 'selectedProduct exists:', !!selectedProduct);
    if (mode === 'edit' && order && selectedProduct && order.id && updatedProduct.id) {
      try {
        const productPayload = {
          name: updatedProduct.name,
          type: updatedProduct.type,
          evaOrder: updatedProduct.evaOrder,
          label: updatedProduct.label,
          location: updatedProduct.location,
          notes: updatedProduct.notes,
          specifications: updatedProduct.specifications,
          itemTypes: transformProductToItemTypes(updatedProduct),
          // Include configuration data with count information
          configuration: (updatedProduct.metadata as any)?.configuration || {
            label: updatedProduct.label,
            location: updatedProduct.location,
            productType: updatedProduct.type,
            color: updatedProduct.color,
            frameChecked: true,
            frameCount: 0,
            panelCount: 0,
            panelWindowCount: 0,
            netCount: 0,
            frameEntries: [],
            panelEntries: [],
            panelWindowEntries: [],
            netEntries: []
          }
        };

        const response = await orderService.updateProductInOrder(order.id, updatedProduct.id, productPayload);
        if (response.success) {
          // Fetch fresh order data from API instead of updating Redux directly
          console.log('🔄 Product updated successfully, fetching fresh order data...');
          const orderResponse = await orderService.getOrder(order.id);
          
          if (orderResponse.success) {
            const updatedOrder = orderResponse.data;
            setOrder(updatedOrder);
            console.log('🔄 Fresh order data fetched:', updatedOrder);
            
            // Trigger refresh
            setRefreshTrigger(prev => prev + 1);
            
            // Call the onOrderUpdated callback if provided
            if (onOrderUpdated) {
              onOrderUpdated(updatedOrder);
            }
          } else {
            console.error('❌ Failed to fetch fresh order data:', orderResponse.error);
            setError('Product updated but failed to refresh order data');
          }
        } else {
          console.error('❌ Failed to update product:', response.error?.message);
        }
      } catch (error) {
        console.error('Error updating product in order:', error);
      }
    }
    setShowItemPanel(false);
    setSelectedProduct(null);
    setIsProductEditMode(false);
  };

  // Add new product to existing order
  const handleAddProductToOrder = async (product: Product) => {
    console.log('🔄 handleAddProductToOrder called with product:', product);
    console.log('🔄 current order:', order);
    console.log('🔄 order.id:', order?.id);
    
    if (!order || !order.id) {
      console.error('❌ No active order found');
      setError('No active order found');
      return;
    }

    // Allow duplicate products - removed duplicate checking

    try {
      // Transform product to the format expected by the API
      const productPayload = {
        name: product.name,
        type: product.type,
        evaOrder: product.evaOrder,
        label: product.label,
        location: product.location,
        notes: product.notes,
        description: (product as any).description,
        specifications: product.specifications,
        itemTypes: transformProductToItemTypes(product)
      };

      console.log('🔄 Calling addProductToOrder with order ID:', order.id);
      console.log('🔄 Product payload:', productPayload);
      const response = await orderService.addProductToOrder(order.id, productPayload);

      if (response.success) {
        // Fetch fresh order data from API instead of updating Redux directly
        console.log('🔄 Product added successfully, fetching fresh order data...');
        const orderResponse = await orderService.getOrder(order.id);
        if (orderResponse.success) {
          const updatedOrder = orderResponse.data;
          setOrder(updatedOrder);
          console.log('🔄 Fresh order data fetched:', updatedOrder);
          
          // Trigger refresh
          setRefreshTrigger(prev => prev + 1);
          
          // Call the onOrderUpdated callback if provided
          if (onOrderUpdated) {
            onOrderUpdated(updatedOrder);
          }
        } else {
          console.error('❌ Failed to fetch fresh order data:', orderResponse.error);
          setError('Product added but failed to refresh order data');
        }
        
        setError(null);
        console.log('✅ Product added successfully:', response.data.product);
        
        // Only clear the panel and reset state on success
        setShowItemPanel(false);
        setSelectedProduct(null);
        setIsProductEditMode(false);
      } else {
        throw new Error(response.error?.message || 'Failed to add product to order');
      }
    } catch (error: any) {
      console.error('Error adding product to order:', error);
      setError('Failed to add product to order. Please try again.');
      
      // Don't clear the panel on error - keep the form open for user to fix
    }
  };

  const handleOrderCreated = (createdOrder: any) => {
    console.log('Order created:', createdOrder);
    if (onOrderCreated) {
      onOrderCreated(createdOrder);
      console.trace(" 🚨 handleSubmit stack");
    }
    setShowItemPanel(false);
    setSelectedProduct(null);
  };

  const handleOrderUpdated = (updatedOrder: Order) => {
    console.log('🔄 UnifiedOrderPage handleOrderUpdated called with:', {
      orderId: updatedOrder.id,
      productCount: updatedOrder.products?.length || 0,
      products: updatedOrder.products
    });
    setOrder(updatedOrder);
    if (onOrderUpdated) {
      onOrderUpdated(updatedOrder);
    }
  };

  // Transform product to itemTypes format for API
  const transformProductToItemTypes = (product: Product) => {
    const itemTypes: Record<string, any[]> = {
      frame: [],
      panel: [],
      panelWindow: [],
      net: [],
      openableType: [],
      door: []
    };

    // Helper function to convert various boolean representations to proper boolean
    const toBoolean = (value: any, defaultValue: boolean): boolean => {
      if (value === null || value === undefined) return defaultValue;
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value !== 0;
      if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
      return defaultValue;
    };

    // Transform items based on their type
    product.items?.forEach(item => {
      // Ensure width and height are integers, not decimals
      const rawWidth = (item as any)?.width  || 0;
      const rawHeight = (item as any)?.height  || 0;
      
      // Convert to integer, handling both string and number inputs
      const width = Math.round(parseFloat(String(rawWidth))) || 0;
      const height = Math.round(parseFloat(String(rawHeight))) || 0;
      
  

      const itemData: any = {
        width: width,
        height: height,
        quantity: item.quantity || 1,
        number: (item as any).number,
        assigned_to: item.assignedTo
      };
      
      // Include item ID if available, or set alreadyExist flag for existing items
      if (item.id) {
        itemData.id = item.id;
      } else if ((item as any).number) {
        // If item has a number but no ID, it's likely an existing item
        itemData.alreadyExist = true;
      }

      // Map item types to the correct category
      switch (item.type as any) {
        case 'Frame':
          itemTypes.frame.push(itemData);
          break;
        case 'Panel':
          itemTypes.panel.push({
            ...itemData,
            // Read actual values from item, with proper defaults
            bar_required: toBoolean((item as any)?.bar_required, false),
            member_required: toBoolean((item as any)?.member_required, true),
            glass_required: toBoolean((item as any)?.glass_required, true),
            is_laser_cutting: toBoolean((item as any)?.is_laser_cutting, false),
          });
          break;
        case 'Panel Window':
          itemTypes.panelWindow.push(itemData);
          break;
        case 'Net Fixing':
          itemTypes.net.push(itemData);
          break;
        case 'Openable Type':
          itemTypes.openableType.push(itemData);
          break;
        case 'Door':
          itemTypes.door.push(itemData);
          break;
        default:
          itemTypes.frame.push(itemData);
      }
    });

    return itemTypes;
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (mode === 'edit' && !order) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Order not found</Alert>
      </Box>
    );
  }

  // Show no permission state if user doesn't have any permissions
  if (!hasAnyPermissions) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        gap={2}
        sx={{ p: 3 }}
      >
        <Alert severity="error" sx={{ width: '100%', maxWidth: 600 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Access Denied
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            You don't have permission to access this page.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please contact your administrator to request the necessary permissions.
          </Typography>
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate("/orders")}
          sx={{ mt: 2 }}
        >
          Back to Orders
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 3 }, 
      display: 'flex', 
      flexDirection: 'column'
    }}>
      {/* Header */}

      <Box sx={{display:'flex', flexDirection:'row'}}>

      
      <OrderHeader
        onBack={handleBack}
        useEnhancedUI={true}
        onToggleUI={() => {}} // No toggle needed for unified page
        title={mode === 'edit' || isEditMode ? `Edit Order: ${order?.orderNumberNew || order?.orderNumber || order?.order_tracking_code || order?.id}` : 'Create Order'}
      />

      <Button
        variant="text"
        size="small"
        onClick={handleViewOrderDetails}
        sx={{ alignSelf: 'center', ml: 'auto', textTransform: 'none', 
          fontSize: '0.75rem',
          color: 'primary.main',
          fontWeight: 600,
          '&:hover': {
            bgcolor: 'primary.light',
            color: 'primary.dark'
          } ,
          mb:4
        }}
        disabled={!order?.id && !id}
      >
        Order Summary
      </Button>
      </Box>


      {/* Success Message */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {/* Main Content */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column'
      }}>
        {mode === 'create' && !isEditMode ? (
          <IncrementalOrderForm
            selectedCustomer={selectedCustomer}
            selectedSite={selectedSite}
            customers={customers}
            sites={sites}
            onCustomerChange={handleCustomerChange}
            onSiteChange={handleSiteChange}
            onProductAdded={handleProductAdded}
            onProductEdit={handleProductEdit}
            onOrderCreated={handleOrderCreated}
            onIncrementalProductComplete={handleItemConfigurationComplete}
            configuredProducts={configuredProducts}
            isEditMode={false}
            existingOrder={null}
          />
        ) : (
          <IncrementalOrderForm
            key={`edit-${order?.id}-${order?.products?.length || 0}`} // Force re-render when order or products change
            selectedCustomer={selectedCustomer}
            selectedSite={selectedSite}
            customers={customers}
            sites={sites}
            onCustomerChange={handleCustomerChange}
            onSiteChange={handleSiteChange}
            onProductAdded={handleProductAdded}
            onProductEdit={handleProductEdit}
            onOrderCreated={handleOrderCreated}
            onOrderUpdated={handleOrderUpdated}
            onIncrementalProductComplete={handleItemConfigurationComplete}
            configuredProducts={[]} // Don't pass order products as configured products in edit mode
            existingOrder={order}
            isEditMode={true}
          />
        )}
      </Box>

      {/* Item Configuration Panel */}
      {showItemPanel && selectedProduct && (
        <>
          {console.log('🔴 Rendering ItemConfigurationPanelWrapper with:', {
            isOpen: showItemPanel,
            product: selectedProduct?.id,
            isEditMode: isProductEditMode,
            hasOnUpdate: !!handleProductUpdate,
            orderStatus: order?.status,
            isReadOnly: isProductEditMode && order && (order.status === 'in_progress' || order.status === 'completed'),
            timestamp: new Date().toISOString()
          })}
          <ItemConfigurationPanelWrapper
            isOpen={showItemPanel}
            onClose={() => setCloseDialog(true)}
            product={selectedProduct}
            onComplete={handleItemConfigurationComplete}
            isEditMode={isProductEditMode}
            onUpdate={handleProductUpdate}
            isIncrementalMode={mode === 'create'}
            onIncrementalComplete={handleItemConfigurationComplete}
            customerId={selectedCustomer?.id}
            siteId={selectedSite?.id}
            orderId={mode === 'edit' ? order?.id : undefined}
            isReadOnly={isProductEditMode && order && (order.status === 'in_progress' || order.status === 'completed')}
            onOrderCreated={(order) => {
              console.log('✅ Order created from ItemConfigurationPanel:', order);
              setOrder(order);
              setSuccessMessage(`Order created successfully! Order Number: ${order.orderNumberNew}`);
              setTimeout(() => setSuccessMessage(null), 5000);
              setIsEditMode(true);
              setConfiguredProducts([]);
              setTimeout(() => {
                navigate(`/orders/edit/${order.id}`);
              }, 2000);
            }}
            onOrderUpdated={handleOrderUpdated}
            onProductAdded={mode === 'edit' ? handleAddProductToOrder : undefined}
          />
        </>
      )}

      {closeDialog && (
        <CloseConfirmationDialogue
          open={closeDialog}
          onClose={() => setCloseDialog(false)}
          onConfirm={() => {            
            setCloseDialog(false);      
            setShowItemPanel(false); 
            setSelectedProduct(null);            
          }}
          title=""
          message="Are you sure you want to close this item model?"
        />
      )}

    </Box>
  );
};

export default UnifiedOrderPage;
